<template>
  <div v-if="canConnectDevices" class="space-y-4">
    <Divider />
    <h2>{{ t('connectDevice') }}</h2>
    <p class="text-sm text-gray-600">{{ t('connectDeviceDescription') }}</p>
    <p class="text-sm text-gray-600">{{ t('connectDeviceLoginHint') }}</p>

    <div class="grid gap-4 md:grid-cols-3">
      <Select
        v-model="selectedRole"
        :label="t('deviceRole')"
        :options="roleOptions"
        :placeholder="t('selectRole')"
      />
      <Select
        v-model="selectedStationId"
        :label="t('station')"
        :disabled="!selectedRole || !isStationRequired"
        :options="stationOptions"
        :placeholder="t('selectStation')"
      />
      <Select
        v-model="selectedCounterId"
        :label="t('counter')"
        :disabled="!selectedStationId || !isCounterRequired"
        :options="counterOptions"
        :placeholder="counterPlaceholder"
      />
    </div>

    <div class="flex items-center gap-3">
      <CVButton :is-loading="isPending" :disabled="!canSubmit" @click="onConnectDevice">
        {{ t('connectDevice') }}
      </CVButton>
      <span v-if="codeResult" class="text-sm text-gray-500">
        {{ t('validUntil') }} {{ formattedExpiresAt }}
      </span>
    </div>

    <div v-if="codeResult" class="rounded-md border border-gray-200 bg-gray-50 p-4">
      <div class="text-xs uppercase tracking-wide text-gray-500">{{ t('authorizationCode') }}</div>
      <div class="mt-2 flex items-center gap-3">
        <code class="text-3xl font-semibold tracking-[0.3em] text-primary">{{
          codeResult.code
        }}</code>
        <CVButton variant="secondary" size="sm" @click="copyCode">
          {{ isCodeCopied ? t('copied') : t('copyCode') }}
        </CVButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAbility } from '@casl/vue';
import Select from '@/components/Select.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Divider from '@/components/Divider.vue';
import { useStations } from '@/queries/use-stations';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { DeviceAuthRole } from '@carvia/ros-client-types';
import { useCreateDeviceAuthCode } from '@/queries/auth/use-device-auth';
import { copyToClipboard } from '@/utils/clipboard';

const { t } = useI18n();
const ability = useAbility();
const { getLocalizedField } = useLocalizedFields();
const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);
const { mutateAsync: createDeviceAuthCode, isPending } = useCreateDeviceAuthCode();
const DEVICE_ROLES_REQUIRING_STATION: DeviceAuthRole[] = [
  DeviceAuthRole.COUNTER_SIGNATURE_IPAD,
  DeviceAuthRole.COUNTER_VIDEO_WALL,
];
const DEVICE_ROLES_REQUIRING_COUNTER: DeviceAuthRole[] = [DeviceAuthRole.COUNTER_SIGNATURE_IPAD];

const selectedRole = ref<DeviceAuthRole | null>(null);
const selectedStationId = ref<string | null>(null);
const selectedCounterId = ref<string | null>(null);
const codeResult = ref<{ code: string; expiresAt: string } | null>(null);
const isCodeCopied = ref(false);

const canConnectDevices = computed(() => ability.can('create', 'DeviceAuth'));

const roleOptions = computed(() => [
  { label: t('roleSalesDashboard'), value: DeviceAuthRole.SALES_DASHBOARD },
  { label: t('roleCounterSignatureIpad'), value: DeviceAuthRole.COUNTER_SIGNATURE_IPAD },
  { label: t('roleCounterVideoWall'), value: DeviceAuthRole.COUNTER_VIDEO_WALL },
]);

const stationOptions = computed(() =>
  stations.value.map((station) => ({
    label: getLocalizedField(station.info),
    value: station.id,
  })),
);

const selectedStation = computed(() =>
  stations.value.find((station) => station.id === selectedStationId.value),
);

const counterOptions = computed(() =>
  (selectedStation.value?.counters ?? []).map((counter) => ({
    label: counter.name,
    value: counter.id,
  })),
);

const isStationRequired = computed(
  () => !!selectedRole.value && DEVICE_ROLES_REQUIRING_STATION.includes(selectedRole.value),
);

const isCounterRequired = computed(
  () => !!selectedRole.value && DEVICE_ROLES_REQUIRING_COUNTER.includes(selectedRole.value),
);

const counterPlaceholder = computed(() =>
  selectedStationId.value && counterOptions.value.length === 0
    ? t('noCountersAtStation')
    : t('selectCounter'),
);

const canSubmit = computed(
  () =>
    !!selectedRole.value &&
    (!isStationRequired.value || !!selectedStationId.value) &&
    (!isCounterRequired.value || !!selectedCounterId.value) &&
    !isPending.value,
);

const formattedExpiresAt = computed(() =>
  codeResult.value ? new Date(codeResult.value.expiresAt).toLocaleString() : '',
);

watch(selectedRole, () => {
  if (!isStationRequired.value) {
    selectedStationId.value = null;
  }
  if (!isCounterRequired.value) {
    selectedCounterId.value = null;
  }
});

watch(selectedStationId, () => {
  selectedCounterId.value = null;
});

const onConnectDevice = async () => {
  if (!selectedRole.value) return;
  const result = await createDeviceAuthCode({
    role: selectedRole.value,
    ...(isStationRequired.value && selectedStationId.value
      ? { stationId: selectedStationId.value }
      : {}),
    ...(isCounterRequired.value && selectedCounterId.value
      ? { counterId: selectedCounterId.value }
      : {}),
  });
  codeResult.value = result;
};

const copyCode = async () => {
  if (!codeResult.value) return;
  await copyToClipboard(codeResult.value.code);
  isCodeCopied.value = true;
  setTimeout(() => {
    isCodeCopied.value = false;
  }, 1500);
};
</script>

<i18n lang="json">
{
  "en": {
    "connectDevice": "Connect device",
    "connectDeviceDescription": "Generate a temporary 6-digit authorization code for a device.",
    "connectDeviceLoginHint": "On the device, open the login page and tap “Sign in with a device pairing code” below the form, then enter this code.",
    "deviceRole": "Device role",
    "station": "Station",
    "counter": "Counter",
    "selectRole": "Select role",
    "selectStation": "Select station",
    "selectCounter": "Select counter",
    "noCountersAtStation": "No counters at this station",
    "authorizationCode": "Authorization code",
    "copyCode": "Copy",
    "copied": "Copied",
    "validUntil": "Valid until",
    "roleSalesDashboard": "Sales dashboard",
    "roleCounterSignatureIpad": "Counter signature iPad",
    "roleCounterVideoWall": "Counter video wall"
  },
  "de": {
    "connectDevice": "Gerät verbinden",
    "connectDeviceDescription": "Generieren Sie einen temporären 6-stelligen Autorisierungscode für ein Gerät.",
    "connectDeviceLoginHint": "Auf dem Gerät die Anmeldeseite öffnen und unten „Mit Gerätekopplungscode anmelden“ wählen, dann diesen Code eingeben.",
    "deviceRole": "Geräterolle",
    "station": "Station",
    "counter": "Schalter",
    "selectRole": "Rolle auswählen",
    "selectStation": "Station auswählen",
    "selectCounter": "Schalter auswählen",
    "noCountersAtStation": "Keine Schalter an dieser Station",
    "authorizationCode": "Autorisierungscode",
    "copyCode": "Kopieren",
    "copied": "Kopiert",
    "validUntil": "Gültig bis",
    "roleSalesDashboard": "Sales Dashboard",
    "roleCounterSignatureIpad": "Counter Signature iPad",
    "roleCounterVideoWall": "Counter Video Wall"
  }
}
</i18n>
